import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { CashFlowsBlotterGridStateKey } from '@approot/models';
import { AgGridService, UserStorageService } from '@approot/services';
import { clone, SubscriptionSink } from '@approot/utilities';
import {
  AgGridEvent,
  ColDef,
  GridReadyEvent,
  SideBarDef
} from 'ag-grid-community';
import { auditTime, distinctUntilChanged, take } from 'rxjs/operators';
import { sorter } from '../shared/utils';
import { applyColumnState } from './cash-flows-blotter.funcs';
import { CashFlowsBlotterService } from './cash-flows-blotter.service';
import {
  CashFlowsBlotterState,
  RunningState
} from './cash-flows-blotter.types';

@Component({
  selector: 'app-cash-flows-blotter',
  templateUrl: './cash-flows-blotter.component.html',
  styleUrls: ['./cash-flows-blotter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CashFlowsBlotterComponent implements OnInit, OnDestroy {
  public sideBarDef: SideBarDef;
  public state: CashFlowsBlotterState = {};
  public entities: string[] = [];
  public runningState: RunningState = RunningState.Idle;
  public RunningState = RunningState;
  public readonly defaultColDef: ColDef = {
    sortable: true,
    enableRowGroup: true,
    resizable: true
  };
  public headers: any[] = [];
  public rowData: any[] = [];
  public readonly overlayNoRowsTemplate =
    '<span style="padding: 10px; border: 2px solid #444;">No cash flows loaded</span>';
  public form: FormGroup = new FormGroup({
    entity: new FormControl<string>(null)
  });

  private subsink = new SubscriptionSink();
  private agGridService = inject(AgGridService);
  private changeRef = inject(ChangeDetectorRef);
  private cashFlowsBlotterService = inject(CashFlowsBlotterService);
  private userStorageService = inject(UserStorageService);

  public constructor() {
    this.sideBarDef = this.agGridService.sideBarDef;
    this.subsink.sink = this.form
      .get('entity')
      .valueChanges.pipe(distinctUntilChanged())
      .subscribe(v =>
        this.cashFlowsBlotterService.update({ selectedEntity: v })
      );
  }

  public ngOnInit(): void {
    // initial state.  Although we resubscribe to stateChanged in onGridReady, we
    // get it immediately (i.e., no `auditTime()`) so that the form is populated with
    // the correct entity, otherwise there's an annoying delay.
    this.cashFlowsBlotterService.stateChanged.pipe(take(1)).subscribe({
      next: state => this.updateOnStateChange(state)
    });
  }

  public ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }

  public onGridReady(params: GridReadyEvent) {
    this.subsink.sink = this.cashFlowsBlotterService.stateChanged
      .pipe(
        auditTime(250) // Don't update too often
      )
      .subscribe({
        next: state => {
          this.updateOnStateChange(state);
        }
      });

    const columnState = this.userStorageService.getItem(
      CashFlowsBlotterGridStateKey
    );
    // sometimes we get a string of 'undefined' from local storage so ignore that
    if (columnState && columnState !== 'undefined') {
      applyColumnState(params.columnApi, columnState);
    }
  }

  public gridStateChanged(event: AgGridEvent) {
    const allState = event.columnApi.getColumnState();
    if (allState) {
      this.userStorageService.setItem(CashFlowsBlotterGridStateKey, allState);
    } else {
      this.userStorageService.removeItem(CashFlowsBlotterGridStateKey);
    }
  }

  public runReport() {
    const entity = this.form.getRawValue().entity as string;
    if (entity == null) {
      return;
    }
    this.cashFlowsBlotterService.runReport(entity);
  }

  public cancelReport() {
    this.cashFlowsBlotterService.cancelReport();
  }

  private updateOnStateChange(state: CashFlowsBlotterState) {
    this.state = state;
    // We don't want to update the grid if the data unchanged
    if (this.rowData.length !== state.rows.length) {
      this.rowData = state.rows;
    }
    if (this.entities.length !== state.entities.length) {
      this.entities = clone(state.entities).sort(sorter);
    }
    if (this.headers.length !== state.headers.length) {
      this.headers = state.headers;
    }
    this.form
      .get('entity')
      .setValue(state.selectedEntity, { emitEvent: false });
    this.changeRef.markForCheck();
  }
}
